import { useEffect, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";
import { ShieldCheck, Eye, Target } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";

const Counter = ({ value, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} data-testid={`counter-${value}${suffix}`}>
      {display}
      {suffix}
    </span>
  );
};

const STATS = [
  { value: 100, suffix: "+", label: "Clients", plain: false },
  { value: 150, suffix: "+", label: "Manufacturing Partners", plain: false },
  { value: 4, suffix: "", label: "Core Product Verticals", plain: false },
];

const VALUES = ["Integrity", "Transparency", "Accountability", "Long-Term Partnership"];

export const About = () => (
  <section id="about" className="relative py-28 lg:py-40 bg-white" data-testid="about-section">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
      <Reveal>
        <SectionTag index="01" label="Who We Are" />
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-7">
          <h2 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0A1B33]" data-testid="about-heading">
            The buyer's extended team,
            <span className="text-[#2E7FE0]"> on the ground</span> in India.
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5 flex flex-col justify-end">
          <p className="text-[#54678A] text-base sm:text-lg leading-relaxed" data-testid="about-copy">
            NexGen Sourcing Private Limited is an India-based sourcing and merchant export
            company. We help clients procure quality-assured products directly from verified
            Indian manufacturers — without the cost and uncertainty of working through multiple
            intermediaries. We identify the right factories, negotiate commercially sound
            pricing, control quality at source, and manage export documentation and logistics
            through to delivery at your port.
          </p>
          <div className="flex flex-wrap gap-3 mt-8" data-testid="about-badges">
            {["Registered in India", "Private Limited Company", "GST Registered"].map((b) => (
              <span
                key={b}
                className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-white bg-[#2E7FE0] border border-[#2E7FE0] rounded-full px-4 py-2 shadow-[0_4px_16px_rgba(46,127,224,0.35)]"
              >
                {b}
              </span>
            ))}
          </div>
        </Reveal>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-[#2E7FE0]/12 border border-[#2E7FE0]/12 mt-20 rounded-2xl overflow-hidden" data-testid="about-stats-grid">
        {STATS.map((s, i) => (
          <div key={s.label} className="bg-white p-8 lg:p-10" data-testid={`about-stat-${i}`}>
            <div className="font-display font-bold text-4xl lg:text-5xl text-[#0A1B33]">
              <Counter value={s.value} suffix={s.suffix} />
            </div>
            <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#54678A] mt-3">
              {s.label}
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Reveal className="glass-panel rounded-2xl p-10 lg:p-12" data-testid="vision-card">
          <Eye className="w-8 h-8 text-[#2E7FE0] mb-6" />
          <h3 className="font-display font-semibold text-2xl text-[#0A1B33] mb-4">Our Vision</h3>
          <p className="text-[#54678A] leading-relaxed">
            To become the most trusted sourcing bridge between Indian manufacturing and global
            buyers — recognised for transparency, consistency and long-term partnership rather
            than one-off transactions.
          </p>
        </Reveal>
        <Reveal delay={0.12} className="glass-panel rounded-2xl p-10 lg:p-12" data-testid="mission-card">
          <Target className="w-8 h-8 text-[#2E7FE0] mb-6" />
          <h3 className="font-display font-semibold text-2xl text-[#0A1B33] mb-4">Our Mission</h3>
          <p className="text-[#54678A] leading-relaxed">
            To simplify India sourcing for our clients by combining a verified supplier network,
            disciplined quality control and complete export execution under a single accountable
            partner.
          </p>
        </Reveal>
      </div>

      <Reveal className="mt-16 flex flex-col sm:flex-row sm:items-center gap-6" data-testid="values-strip">
        <div className="flex items-center gap-3 shrink-0">
          <ShieldCheck className="w-5 h-5 text-[#2E7FE0]" />
          <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#54678A]">
            The values we operate on
          </span>
        </div>
        <div className="flex flex-wrap gap-3">
          {VALUES.map((v, i) => (
            <span
              key={v}
              className="font-display font-medium text-[#0A2A5E] bg-[#E8F0FC] border border-[#2E7FE0]/25 rounded-full px-6 py-2.5"
              data-testid={`value-chip-${i}`}
            >
              {v}
            </span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);
